import React, { useState } from 'react';
import { Card, CardHeader, CardContent, Button, Checkbox, FormControlLabel } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const SMSComplianceAttestation = ({ onEnableSMS, onDisableSMS, isSMSEnabled, styles, setIsAttested, isAttested }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleEnableSMS = async () => {
    if (!isAttested) return;
    
    setIsLoading(true);
    try {
      await onEnableSMS();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableSMS = async () => {
    setIsLoading(true);
    try {
      await onDisableSMS();
      setIsAttested(false);
    } finally {
      setIsLoading(false);
    }
  };

   return (
    <div
      className="mb-14 mt-30"
    >
      <Card
        className={`${styles.notificationCard} dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:text-white dark:border-none`}
      >
        <CardHeader
          avatar={
            isSMSEnabled ? (
              <CheckCircleOutlineIcon
                style={{ color: "#078037", fontSize: "28px" }}
              />
            ) : (
              <WarningAmberOutlinedIcon
                style={{ color: "#F59E0B", fontSize: "28px" }}
              />
            )
          }
          title={
            <span
              className="text-xl text-foreground"
            >
              SMS Messaging Compliance Attestation
            </span>
          }
          subheader={
            <span
             className='text-[#64748B] dark:text-white mt-14'
            >
              {isSMSEnabled
                ? "SMS messaging is currently enabled for your company. You can disable it at any time."
                : "This attestation is required once per company and applies to all authorized users. SMS messaging will be disabled until completed."}
            </span>
          }
          className={styles.cardHeader}
        />
        <CardContent className={styles.cardContent}>
          <div style={{ padding: "0 16px" }}>
            {isSMSEnabled ? (
              <div>
                <div
                  style={{
                    backgroundColor: "#D1FAE5",
                    border: "1px solid #6EE7B7",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "24px",
                  }}
                  className="dark:bg-[#064E3B] dark:border-[#047857]"
                >
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#047857",
                      margin: "0",
                      fontWeight: "500",
                    }}
                    className="dark:text-[#6EE7B7]"
                  >
                    ✓ SMS messaging has been enabled. Your company has attested to compliance with all applicable laws and regulations. Authorized users can now send SMS notifications to employees.
                  </p>
                </div>

                <Button
                  variant="outlined"
                  onClick={handleDisableSMS}
                  disabled={isLoading}
                  style={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "10px 24px",
                  }}
                  className="hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  {isLoading ? "Disabling..." : "Disable SMS Messaging"}
                </Button>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    backgroundColor: "#FEF3C7",
                    border: "1px solid #FCD34D",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "24px",
                  }}
                  className="dark:bg-[#422006] dark:border-[#92400E]"
                >
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#92400E",
                      margin: "0 0 12px 0",
                    }}
                    className="dark:text-[#FCD34D]"
                  >
                    By enabling SMS messaging, I represent and warrant that I am an
                    authorized representative of this company and that the company
                    has obtained all legally required consent from all current and
                    future employees to receive SMS messages sent through this
                    platform.
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#92400E",
                      margin: "0 0 12px 0",
                    }}
                    className="dark:text-[#FCD34D]"
                  >
                    I acknowledge that SMS messages may be sent by any authorized
                    user of this account for operational, scheduling, safety, and
                    service-related communications.
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#92400E",
                      margin: "0",
                    }}
                    className="dark:text-[#FCD34D]"
                  >
                    I confirm that all SMS messages sent through this platform will
                    include legally required opt-out instructions (e.g., "Reply STOP
                    to opt out") and that the company is solely responsible for
                    compliance with all applicable laws, regulations, and carrier
                    requirements, including but not limited to the Telephone Consumer
                    Protection Act (TCPA).
                  </p>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAttested}
                        onChange={(e) => setIsAttested(e.target.checked)}
                        style={{
                          color: isAttested ? "#078037" : "#64748B",
                        }}
                      />
                    }
                    label={
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                        className={`dark:text-white ${isAttested ? 'text-[#078037]' : 'text-[#1E293B]'}`}
                      >
                        I am an authorized representative of this company and I attest
                        on its behalf.
                      </span>
                    }
                  />
                </div>

                <Button
                  variant="contained"
                  onClick={handleEnableSMS}
                  disabled={!isAttested || isLoading}
                  style={{
                    backgroundColor: isAttested ? "#078037" : "#94A3B8",
                    color: "white",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "10px 24px",
                  }}
                >
                  {isLoading ? "Enabling..." : "Enable SMS Messaging"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SMSComplianceAttestation;